import { CallStatus, isTwilioCallLegUnanswered } from '../../../../definitions';

/** CallStatus values that mean the call is over (no longer in progress). */
const TERMINAL_CALL_STATUSES: ReadonlySet<CallStatus> = new Set([
  CallStatus.ENDED,
  CallStatus.MISSED,
  CallStatus.BUSY,
  CallStatus.FAILED,
]);

/** Whether the call is still live (CONNECTING or ACTIVE). */
export function isCallInProgress(status: CallStatus): boolean {
  return status === CallStatus.CONNECTING || status === CallStatus.ACTIVE;
}

/** Whether the call reached a terminal status (ENDED/MISSED/BUSY/FAILED). */
export function isCallTerminal(status: CallStatus): boolean {
  return TERMINAL_CALL_STATUSES.has(status);
}

/** Whether the call ended in a failure (never successfully connected). */
export function isCallFailed(status: CallStatus): boolean {
  return status === CallStatus.FAILED;
}

/** Minimal call fields for ended-state UI (dock dismiss, post-call entry). */
export interface CallEndedStateFields {
  timeEnded?: number | null;
  status?: CallStatus | null;
}

/**
 * Whether UI should treat the call as ended. Status may arrive before timeEnded
 * is persisted on the DTO.
 * @param call - Call snapshot with optional timeEnded and status.
 * @returns True when timeEnded is set or status is terminal.
 */
export function isCallEndedForUi(
  call: CallEndedStateFields | null | undefined
): boolean {
  return (
    !!call?.timeEnded ||
    (!!call?.status && isCallTerminal(call.status))
  );
}

/** Minimal connection shape used by connection-level predicates. */
export interface CallConnectionStateFields {
  status: string;
  connectedAt?: number | null;
  completedAt?: number | null;
}

/** Whether a connection has been answered and has not yet ended. */
export function isConnectionAnswered(
  connection: CallConnectionStateFields
): boolean {
  return !!connection.connectedAt && !connection.completedAt;
}

/** Whether a connection is still live (not completed and not a dead leg). */
export function isConnectionActive(
  connection: CallConnectionStateFields
): boolean {
  return (
    !connection.completedAt && !isTwilioCallLegUnanswered(connection.status)
  );
}

/** Whether a connection is ringing (dialed, not yet answered, not dead/completed). */
export function isConnectionRinging(
  connection: CallConnectionStateFields
): boolean {
  return (
    !connection.connectedAt &&
    !connection.completedAt &&
    !isTwilioCallLegUnanswered(connection.status)
  );
}

/** Whether at least one connection is currently answered/live. */
export function hasAnsweredConnection(
  connections: CallConnectionStateFields[]
): boolean {
  return connections.some(isConnectionAnswered);
}
